/**
 * 我的练习: what the trainee started, newest first: continue one, read its
 * report, or play the same customer again (再练一次).
 */

import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import { SecondaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { CoachingNavParams } from '@euler/app/flows/coaching';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  CoachingAttemptStatus,
  CoachingAttemptView,
} from '@euler/model/coaching';
import { useServiceFactory } from '@euler/services/factory';
import {
  NUMERIC_ALPHABETIC_LOWER_CHARACTERS,
  makeUniqueIdCustom,
} from '@euler/utils/id';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  RefreshControl,
  View,
} from 'react-native';
import { useAttemptHistory } from '../../functions/useAttemptHistory';
import { ProgressHeader } from '../../progress/ProgressHeader';
import { GoalSheet } from '../../progress/components/GoalSheet';
import { useCoachingProfile } from '../../progress/functions/useCoachingProfile';
import { useCoachingStreak } from '../../progress/functions/useCoachingStreak';
import { AttemptRow } from './AttemptRow';

const keyOf = (item: CoachingAttemptView) => item.id;

export const CoachingHistoryScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const { coachingService } = useServiceFactory();
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    const { attempts, failed, pageFailed, loadingMore, reload, loadMore } =
      useAttemptHistory();
    const progress = useCoachingProfile();
    const streak = useCoachingStreak();
    const [editingGoal, setEditingGoal] = useState(false);
    const { reload: reloadProfile } = progress;
    const { reload: reloadStreak } = streak;
    const refresh = useCallback(() => {
      reload();
      reloadProfile();
      reloadStreak();
    }, [reload, reloadProfile, reloadStreak]);
    // Back from a practice or its report: what it earned shows at once.
    const focused = useRef(false);
    useFocusEffect(
      useCallback(() => {
        if (focused.current) {
          reload();
          reloadProfile();
          reloadStreak();
        }
        focused.current = true;
      }, [reload, reloadProfile, reloadStreak]),
    );
    const [replaying, setReplaying] = useState<ReadonlySet<string>>(
      () => new Set(),
    );
    const [replayFailed, setReplayFailed] = useState(false);
    // One key per attempt until its replay succeeds: a double tap or a
    // retry after a lost response is the same replay, not a second one.
    const replayKeys = useRef(new Map<string, string>());
    const inFlight = useRef(new Set<string>());

    const replay = useCallback(
      async (attempt: CoachingAttemptView) => {
        if (inFlight.current.has(attempt.id)) return;
        inFlight.current.add(attempt.id);
        const key =
          replayKeys.current.get(attempt.id) ??
          makeUniqueIdCustom(NUMERIC_ALPHABETIC_LOWER_CHARACTERS, 24);
        replayKeys.current.set(attempt.id, key);
        setReplaying(new Set(inFlight.current));
        setReplayFailed(false);
        try {
          const started = await coachingService.replayAttempt(attempt.id, key);
          replayKeys.current.delete(attempt.id);
          // Straight into it, as 再练一次 does: a replay that only showed up
          // in the list read as a tap that did nothing, and was tapped again.
          navigation.push('practice', { attemptId: started.id });
        } catch (error) {
          console.error('coaching replay failed', error);
          setReplayFailed(true);
        } finally {
          inFlight.current.delete(attempt.id);
          setReplaying(new Set(inFlight.current));
        }
      },
      [coachingService, navigation],
    );

    const renderItem = useCallback<ListRenderItem<CoachingAttemptView>>(
      ({ item }) => (
        <AttemptRow
          attempt={item}
          replaying={replaying.has(item.id)}
          onReplay={() => replay(item)}
          onOpen={() =>
            navigation.push(
              item.status === CoachingAttemptStatus.Ready ||
                item.status === CoachingAttemptStatus.Active
                ? 'practice'
                : 'report',
              { attemptId: item.id },
            )
          }
        />
      ),
      [navigation, replay, replaying],
    );

    if (failed && attempts == null)
      return <EmptyView message="练习记录暂时没加载出来" onRetry={reload} />;
    if (attempts == null)
      return (
        <ActivityIndicator
          color={aiColors.primary}
          style={{ marginTop: rpx(48) }}
        />
      );
    return (
      <>
        <FlatList
          data={attempts}
          keyExtractor={keyOf}
          renderItem={renderItem}
          style={{ flex: 1 }}
          contentContainerStyle={{
            width: '100%',
            maxWidth: rpx(768),
            alignSelf: 'center',
            padding: rpx(16),
            gap: rpx(10),
          }}
          refreshControl={
            <RefreshControl refreshing={false} onRefresh={refresh} />
          }
          onEndReachedThreshold={0.4}
          onEndReached={loadMore}
          ListHeaderComponent={
            <View style={{ gap: rpx(10) }}>
              <ProgressHeader
                streak={streak.streak}
                profile={progress.profile}
                onEditGoal={() => setEditingGoal(true)}
                onOpenLeaderboard={() => navigation.push('leaderboard', {})}
              />
              {progress.failed || streak.failed ? (
                <CardNotice
                  tone="danger"
                  text="练习进度没加载出来，下拉刷新试试。"
                />
              ) : null}
              {replayFailed || failed ? (
                <CardNotice
                  tone="danger"
                  text={
                    failed
                      ? '练习记录没刷新成功，请稍后再试。'
                      : '没能再练一次，请稍后再试。'
                  }
                />
              ) : null}
            </View>
          }
          ListEmptyComponent={
            <EmptyView message="还没有练习，去选一个场景吧" noFullHeight />
          }
          ListFooterComponent={
            loadingMore ? (
              <View style={{ paddingVertical: rpx(16) }}>
                <ActivityIndicator color={aiColors.primary} />
              </View>
            ) : pageFailed ? (
              <SecondaryButton
                title="没加载出来，点此重试"
                onPress={loadMore}
              />
            ) : null
          }
        />
        {streak.streak ? (
          <GoalSheet
            visible={editingGoal}
            current={streak.streak.weeklyGoal}
            saving={streak.saving}
            onClose={() => setEditingGoal(false)}
            onSave={streak.setGoal}
          />
        ) : null}
      </>
    );
  },
  { title: '我的练习' },
);
